import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"

import { useTranslate } from "context/translateContext"
import {
  usePlansQuery,
  useSubscribedPlansQuery,
} from "data/queries/users/plans"
import Button from "shared/components/Button/Button"
import Container from "shared/components/Container"
import ErrorComponent from "shared/components/ErrorComponent"
import Loader from "shared/components/Loader/Local"

import Domestic from "./components/Domestic/Domestic"
import International from "./components/International"
import PlanChangeModal from "./components/PlanChangeModal/PlanChangeModal"
import PlansListingSwitch from "./components/PlansListingSwitch/PlansListingSwitch"
import PlansSwitch from "./components/PlansSwitch"
import usePlans from "./hooks/usePlans"
import useUpdatePlan from "./hooks/useUpdatePlan"
import Asterisk from "./views/International/Asterisk"
import LegacyPlanAlert from "./views/LegacyPlanAlert"
import Contact from "./views/Contact/Contact"

import styles from "./Plans.module.css"
import FAQ from "./views/FAQ/FAQ"



const Plans = () => {
  const { t: translate } = useTranslate()
  const userId = useSelector(state => state.user.id)
  const lang = useSelector(state => state.i18nState.lang)
  const [showChangeModal, setShowChangeModal] = useState(false)
  const [newPlanName, setNewPlanName] = useState("")
  const { pathname } = useLocation()
  const { getPlans, getLegacyPlan } = usePlans()
  const { loading: changePlanLoading, updateUserPlan } = useUpdatePlan(userId)
  const availablePlans = usePlansQuery(userId)
  const subscribedPlans = useSubscribedPlansQuery(userId)

  // Adding this because React.StrictMode prevents react-modal to
  // remove "ReactModal__Body--open" css class from body
  useEffect(() => {
    if (!showChangeModal) {
      window.document.body.classList = ""
    }
  }, [showChangeModal])

  const onChangePlanRequest = async () => {
    const reqData = {
      new_product_plan_name: newPlanName,
    }
    await updateUserPlan(reqData)
    await availablePlans.refetch()
    onCloseChangeModal()
  }

  const onCloseChangeModal = () => {
    setShowChangeModal(false)
    setNewPlanName(null)
  }

  const onChangePlan = planName => {
    setShowChangeModal(true)
    setNewPlanName(planName)
  }

  const isDomestic = pathname === "/plans/domestic"
  const isMonthly = pathname === "/plans/monthly"
  const plans = getPlans(availablePlans)
  const legacyPlan = getLegacyPlan(subscribedPlans)
  return (
    <Container fullWidth className={styles.planContainer}>
      <div>
        <header className={styles.header}>
          <div className={styles.headerBox}>
            <h1>{translate("Plans")}</h1>
            <h2>{translate("Pick your plan to increase your sales")}</h2>
            <PlansListingSwitch isDomestic={isDomestic} />
          </div>
        </header>
        <Container className="m-b-50">
          {subscribedPlans.data ? (
            <LegacyPlanAlert legacyPlanName={legacyPlan?.title} />
          ) : null}
          {availablePlans.isInitialLoading ? (
            <div className="m-t-50 m-b-50">
              <Loader floating={false} />
            </div>
          ) : null}
          {availablePlans.isError ? (
            <div className="relative p-v-50 m-t-60">
              <ErrorComponent
                action={availablePlans.refetch}
                message="We couldn't get the offer for you right now. Please try again in a moment!"
              />
            </div>
          ) : null}

          {availablePlans.data ? (
            <Routes>
              <Route path="/">
                <Route index element={<Navigate to="monthly" replace />} />
                <Route
                  path="domestic"
                  element={
                    <>
                      <div className={styles.switcherContainer}>
                        <Button to="/" variation="textLink">
                          <div className={styles.buttonLabel}>
                            <span className="icon-arrow-left" />
                            {translate("Go back to dashboard")}
                          </div>
                        </Button>
                      </div>
                      <Domestic
                        data={plans}
                        lang={lang}
                        onChangePlan={onChangePlan}
                      />
                    </>
                  }
                />
                <Route
                  path=":type"
                  element={
                    <>
                      <div className={styles.switcherContainer}>
                        <Button to="/" variation="textLink">
                          <div className={styles.buttonLabel}>
                            <span className="icon-arrow-left" />
                            {translate("Go back to dashboard")}
                          </div>
                        </Button>
                      </div>
                      <PlansSwitch />

                      <International
                        data={plans}
                        lang={lang}
                        onChangePlan={onChangePlan}
                      />

                      {isMonthly && <Asterisk
                        minimalMonthlyChargeValue={
                          plans?.minimum_monthly_charge
                        }
                        lang={lang}
                      />}
                    </>
                  }
                />
              </Route>
            </Routes>
          ) : null}
          {showChangeModal ? (
            <PlanChangeModal
              submitting={changePlanLoading}
              onClose={onCloseChangeModal}
              onSubmit={onChangePlanRequest}
            />
          ) : null}
          <FAQ />
          <Contact lang={lang} />
        </Container>
      </div>
    </Container>
  )
}

export default Plans
